import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Fields the system already knows and should hide from the member.
const AUTO_FIELDS = new Set([
  'una_name',
  'legal_name',
  'ca_filing_number',
  'una_ein',
  'una_address',
  'resolution_number',
  'signature_date',
  'signing_officer_name',
  'signing_officer_office',
]);

export interface StandardResolutionRow {
  id: string;
  template_name: string;
  description: string | null;
  template_content: string;
  merge_fields: any;
}

interface MergeField {
  name: string;
  label?: string;
  type?: string;
  required?: boolean;
}

interface Props {
  onUseStandard: (row: StandardResolutionRow, values: Record<string, string>) => void;
  disabled?: boolean;
}

function normalizeFields(raw: any): MergeField[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((f: any) => (typeof f === 'string' ? { name: f } : f))
      .filter((f: any) => f && f.name);
  }
  if (typeof raw === 'object') {
    return Object.entries(raw).map(([name, spec]: any) => ({
      name,
      ...(typeof spec === 'object' ? spec : {}),
    }));
  }
  return [];
}

export function StandardResolutionPicker({ onUseStandard, disabled }: Props) {
  const [rows, setRows] = useState<StandardResolutionRow[] | null>(null);
  const [selected, setSelected] = useState<StandardResolutionRow | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('document_templates')
        .select('id, template_name, description, template_content, merge_fields, status, is_active, template_type')
        .eq('template_type', 'resolution')
        .eq('status', 'published')
        .eq('is_active', true);
      if (cancelled) return;
      if (error) {
        setRows([]);
        return;
      }
      setRows((data as any[]) || []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!rows || rows.length === 0) return null;

  const fields = selected ? normalizeFields(selected.merge_fields).filter(f => !AUTO_FIELDS.has(f.name)) : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Standard resolutions</CardTitle>
        <p className="text-sm text-muted-foreground">
          Prepared forms you fill in. The sample documents in Support show completed examples.
        </p>
      </CardHeader>
      <CardContent>
        {!selected ? (
          <div className="space-y-3">
            {rows.map((r) => (
              <div
                key={r.id}
                className="flex items-start justify-between gap-3 border rounded-lg p-3"
              >
                <div className="min-w-0">
                  <div className="font-medium">{r.template_name}</div>
                  {r.description && (
                    <div className="text-sm text-muted-foreground mt-1">{r.description}</div>
                  )}
                </div>
                <Button
                  size="sm"
                  disabled={disabled}
                  onClick={() => {
                    setSelected(r);
                    setValues({});
                  }}
                >
                  Use this
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-medium">{selected.template_name}</div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelected(null);
                  setValues({});
                }}
              >
                Write your own
              </Button>
            </div>
            <div className="space-y-3">
              {fields.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  All fields for this form are prefilled from your association records.
                </p>
              )}
              {fields.map((f) => {
                const label = f.label || f.name.replace(/_/g, ' ');
                const isLong = f.type === 'textarea' || /clause|content|body|notes/i.test(f.name);
                return (
                  <div key={f.name} className="space-y-1">
                    <Label>
                      {label}
                      {f.required ? ' *' : ''}
                    </Label>
                    {isLong ? (
                      <Textarea
                        rows={3}
                        value={values[f.name] || ''}
                        onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                      />
                    ) : (
                      <Input
                        type={f.type === 'date' ? 'date' : 'text'}
                        value={values[f.name] || ''}
                        onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end">
              <Button
                disabled={disabled}
                onClick={() => onUseStandard(selected, values)}
              >
                Continue with this form
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
